import React, { ButtonHTMLAttributes } from 'react';
import html2canvas from 'html2canvas';
import DownloadButton from 'src/shared/components/download-button/DownloadButton';
import useBoolean from 'src/shared/hooks/useBoolean';
import { downloadBlobAsFile } from 'src/shared/helpers/downloadAsFile';
import { Download as DownloadIcon } from 'lucide-react';

export type DownloadImageButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    containerRef?: React.RefObject<HTMLElement | null>;
    containerElement?: HTMLElement | null;
    downloadFileName?: string;
    buttonClassName?: string;
    scale?: number;
    ignoreElements?: (element: Element) => boolean;
    onClone?: (document: Document, element: Element) => void;
};

const DownloadImageButton = (props: DownloadImageButtonProps) => {
    const {
        containerRef,
        containerElement,
        downloadFileName,
        ignoreElements,
        onClone,
        buttonClassName,
        className,
        onClick,
        scale = 1.2,
    } = props;
    const [isDownloading, setIsDownloading] = useBoolean(false);

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        // Prevent multiple downloads
        if (isDownloading) {
            return;
        }

        // Set the downloading state
        setIsDownloading.on();

        try {
            const cloneElement = containerElement || containerRef?.current;
            if (!cloneElement) {
                throw new Error('Invalid container element for image download');
            }

            // Create a canvas of the container element
            const containerCanvas = await html2canvas(cloneElement, {
                useCORS: true,
                scale: scale,
                allowTaint: true,
                ignoreElements: ignoreElements,
                onclone: onClone,
                logging: false,
            });

            // Convert the canvas to a blob and create a download link
            containerCanvas.toBlob(blob => {
                if (blob) {
                    downloadBlobAsFile(blob, downloadFileName || 'NatureMetrics_Image_File.png');
                } else {
                    throw new Error('Failed to create blob from canvas');
                }
            });
        } catch (error) {
            console.error('Error downloading image', error);
        } finally {
            // Reset the downloading state
            setIsDownloading.off();
        }

        // Call the props onClick function if it exists
        onClick?.(e);
    };

    return (
        <DownloadButton className={className} buttonClassName={buttonClassName} onClick={handleClick} disabled={isDownloading}>
            <DownloadIcon />
            <span>Download image</span>
        </DownloadButton>
    );
};

export default DownloadImageButton;
